import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { MailboxEvent } from "@onemileauto/shared-types";
import { mailboxQueryKeys } from "../lib/query-keys";
import { useOutboxToastDispatch } from "./use-outbox-toasts";

/**
 * Real-time thread-list/detail updates over the MailboxDO's Hibernation-API
 * WebSocket (see apps/mail/src/routes/ws.ts + durable-objects/mailbox-do.ts
 * #broadcast). This is the "no manual refresh" requirement from the
 * architecture plan.
 *
 * Deliberately simple cache strategy: on any mutating event, invalidate the
 * relevant TanStack Query groups (by key prefix — see lib/query-keys.ts) so
 * they refetch, rather than hand-splicing the event payload into paginated
 * infinite-query caches. For this mailbox's real traffic volume a refetch
 * is cheap and this avoids a whole class of "merged the DTO into the cache
 * slightly wrong" bugs; a future pass can move to fully local cache patches
 * if it ever needs to shave the round trip.
 */
export function useMailboxSocket(mailboxId: string | undefined): void {
  const queryClient = useQueryClient();
  const outboxDispatch = useOutboxToastDispatch();

  useEffect(() => {
    if (!mailboxId) return;
    if (typeof window === "undefined") return;

    let cancelled = false;
    let socket: WebSocket | null = null;
    let retryTimer: number | undefined;
    let retryDelayMs = 1000;

    function connect() {
      if (cancelled) return;
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${protocol}://${window.location.host}/api/mailboxes/${mailboxId}/ws`;

      try {
        socket = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      socket.addEventListener("open", () => {
        retryDelayMs = 1000;
      });

      socket.addEventListener("message", (event) => {
        if (typeof event.data !== "string") return;
        let payload: MailboxEvent;
        try {
          payload = JSON.parse(event.data) as MailboxEvent;
        } catch {
          return;
        }
        handleMailboxEvent(queryClient, mailboxId!, payload);
        outboxDispatch({ type: "wsEvent", event: payload });
      });

      socket.addEventListener("close", scheduleReconnect);
      socket.addEventListener("error", () => socket?.close());
    }

    function scheduleReconnect() {
      if (cancelled) return;
      retryTimer = window.setTimeout(connect, retryDelayMs);
      retryDelayMs = Math.min(retryDelayMs * 2, 30_000);
    }

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [mailboxId, queryClient, outboxDispatch]);
}

function handleMailboxEvent(queryClient: QueryClient, mailboxId: string, event: MailboxEvent): void {
  switch (event.type) {
    case "message:new":
    case "thread:moved":
    case "message:sending":
    case "message:sent":
    case "message:cancelled":
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadsAll(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.thread(mailboxId, event.threadId) });
      break;
    case "thread:updated":
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadsAll(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.thread(mailboxId, event.thread.id) });
      break;
    case "message:failed":
      // No list-level change (the message stays where it is, just marked
      // failed) — only the open thread detail needs to refresh to show it.
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.thread(mailboxId, event.threadId) });
      break;
    case "folder:created":
    case "folder:updated":
      // The folder nav (useFolders) is what needs to see this — its
      // 5-minute staleTime ("folders basically never change") is fine for
      // a solo user's own edits, which already land optimistically-ish via
      // this same event round-trip, but another connected client (or
      // another tab) needs the invalidation to pick it up live.
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.folders(mailboxId) });
      break;
    case "folder:deleted":
      // Threads that were in the deleted folder already moved to Archive
      // server-side (see MailboxDO#deleteFolder) — broad thread
      // invalidation here is simpler and safer than hand-tracking which
      // folder views are affected, same call useMoveThread's onSettled
      // already makes for a single-thread move.
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.folders(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadsAll(mailboxId) });
      break;
  }
}
